import Phaser from 'phaser';
import { CardData, Rarity } from '../data/cards';
import { RESOURCES } from '../scenes/GameScene';
import { GAME_WIDTH } from '../scenes/BootScene';

// Card dimensions - COMPACT to see horses better
const CARD_WIDTH = 100;
const CARD_HEIGHT = 130;

// Color palette matching Pryor Mountain backgrounds
const COLORS = {
  bone: 0xD2CFBF,        // Light tan/bone
  cornflower: 0x6B94E4,  // Sky blue
  oliveWood: 0x8B5A2B,   // Brown wood
  deepForest: 0x163F29,  // Dark green
  cream: 0xE8E4D8,       // Soft cream
  textDark: 0x2F3D2F,    // Dark forest text
  success: 0x4A7C59,     // Muted green
  danger: 0xA65D57,      // Muted red
  gold: 0xC9A227,        // Warm gold
};

// Rarity colors for card borders
const RARITY_COLORS: Record<Rarity, number> = {
  common: COLORS.oliveWood,
  uncommon: COLORS.cornflower,
  rare: 0x9370DB,
  legendary: COLORS.gold
};

const RARITY_GLOW: Record<Rarity, number> = {
  common: COLORS.oliveWood,
  uncommon: 0x87CEEB,
  rare: 0xBA55D3,
  legendary: 0xFFD700
};

export class Card extends Phaser.GameObjects.Container {
  public cardData: CardData;
  private homeX: number;
  private homeY: number;
  private bg: Phaser.GameObjects.Rectangle;
  private isHovered: boolean = false;
  private isDragging: boolean = false;
  private glowEffect?: Phaser.GameObjects.Rectangle;

  constructor(scene: Phaser.Scene, x: number, y: number, cardData: CardData, index: number) {
    super(scene, x, y);
    
    this.cardData = cardData;
    this.homeX = x;
    this.homeY = y;
    
    // Card background with rarity-based border
    const rarityColor = RARITY_COLORS[cardData.rarity] || COLORS.oliveWood;
    const bgColor = cardData.rarity === 'legendary' ? 0xFFFACD : COLORS.cream;
    
    // Glow effect for rare+ cards
    if (cardData.rarity === 'rare' || cardData.rarity === 'legendary') {
      this.glowEffect = scene.add.rectangle(0, 0, CARD_WIDTH + 12, CARD_HEIGHT + 12, RARITY_GLOW[cardData.rarity], 0.4);
      this.add(this.glowEffect);
      
      // Pulse animation for legendaries
      if (cardData.rarity === 'legendary') {
        scene.tweens.add({
          targets: this.glowEffect,
          alpha: 0.7,
          scaleX: 1.08,
          scaleY: 1.08,
          duration: 800,
          yoyo: true,
          repeat: -1
        });
      }
    }
    
    // Main card background
    this.bg = scene.add.rectangle(0, 0, CARD_WIDTH, CARD_HEIGHT, bgColor)
      .setStrokeStyle(4, rarityColor);
    
    // Inner border for polish
    const innerBorder = scene.add.rectangle(0, 0, CARD_WIDTH - 8, CARD_HEIGHT - 8)
      .setStrokeStyle(1, rarityColor, 0.3)
      .setFillStyle(0x000000, 0);
    
    // Card type indicator bar at top
    const typeColors: Record<string, number> = {
      horse: 0x8B4513,
      action: 0x4169E1,
      event: 0xDC143C,
      infrastructure: 0x228B22,
      legendary: 0xFFD700
    };
    const typeBar = scene.add.rectangle(0, -CARD_HEIGHT / 2 + 6, CARD_WIDTH - 12, 8, typeColors[cardData.type] || 0x888888);
    
    // Rarity stars
    let rarityStars = '';
    if (cardData.rarity === 'uncommon') rarityStars = '★';
    if (cardData.rarity === 'rare') rarityStars = '★★';
    if (cardData.rarity === 'legendary') rarityStars = '★★★';
    
    const rarityText = scene.add.text(0, -CARD_HEIGHT / 2 + 20, rarityStars, {
      fontSize: '12px',
      color: cardData.rarity === 'legendary' ? '#FFD700' : '#666666'
    }).setOrigin(0.5);
    
    // Card title - compact with nice font
    const titleColor = cardData.rarity === 'legendary' ? '#8B4513' : '#2F4F4F';
    const title = scene.add.text(0, -CARD_HEIGHT / 2 + 32, cardData.title, {
      fontFamily: 'Lora, Georgia, serif',
      fontSize: '10px',
      color: titleColor,
      align: 'center',
      wordWrap: { width: CARD_WIDTH - 10 },
      fontStyle: 'bold'
    }).setOrigin(0.5);
    
    // Card art (emoji) - centered
    const art = scene.add.text(0, 5, cardData.art, {
      fontSize: '32px'
    }).setOrigin(0.5);
    
    // NO description on card - will show in drop zone
    // Store description for later use
    (this as any).cardDescription = cardData.description || '';
    
    // Cost indicator at bottom
    let costText: Phaser.GameObjects.Text | null = null;
    if (cardData.cost && cardData.cost > 0) {
      costText = scene.add.text(0, CARD_HEIGHT / 2 - 18, `$${Math.floor(cardData.cost / 1000)}k`, {
        fontFamily: 'Source Sans 3, sans-serif',
        fontSize: '10px',
        fontStyle: 'bold',
        color: '#DAA520'
      }).setOrigin(0.5);
    }
    
    
    // Trait indicator (bottom right)
    let traitBadge: Phaser.GameObjects.Text | null = null;
    if (cardData.trait) {
      const traitEmojis: Record<string, string> = {
        dun: '🟫',
        grulla: '🩶',
        buckskin: '🟡',
        leader: '👑',
        hardy: '💪',
        swift: '⚡',
        gentle: '💕',
        wild: '🔥'
      };
      traitBadge = scene.add.text(CARD_WIDTH / 2 - 20, CARD_HEIGHT / 2 - 18, traitEmojis[cardData.trait] || '', {
        fontSize: '16px'
      }).setOrigin(0.5);
    }
    
    // Add all elements
    this.add([this.bg, innerBorder, typeBar, rarityText, title, art]);
    if (costText) this.add(costText);
    if (traitBadge) this.add(traitBadge);
    
    this.setSize(CARD_WIDTH, CARD_HEIGHT);
    scene.add.existing(this);
    
    // Make interactive
    this.setInteractive({ draggable: true, useHandCursor: true });
    
    // Hover effects
    this.on('pointerover', () => {
      if (this.isDragging) return;
      this.isHovered = true;
      scene.tweens.add({
        targets: this,
        scaleX: 1.12,
        scaleY: 1.12,
        y: this.homeY - 35,
        duration: 150,
        ease: 'Back.easeOut'
      });
      this.setDepth(600);
    });
    
    this.on('pointerout', () => {
      if (this.isDragging) return;
      this.isHovered = false;
      scene.tweens.add({
        targets: this,
        scaleX: 1,
        scaleY: 1,
        y: this.homeY,
        duration: 150
      });
      this.setDepth(500 + index);
    });
    
    // Drag start - emit sound event and show description
    this.on('dragstart', () => {
      this.isDragging = true;
      scene.events.emit('cardPickup');
      // Show card description in play zone
      const gameScene = scene as any;
      if (gameScene.updatePlayZoneDescription) {
        gameScene.updatePlayZoneDescription(cardData.description || cardData.title);
      }
    });
    
    // Drag handling
    this.on('drag', (pointer: Phaser.Input.Pointer, dragX: number, dragY: number) => {
      this.x = dragX;
      this.y = dragY;
      this.setDepth(1000);
      
      // Visual feedback when over play zone
      if (this.y < 180) {
        this.bg.setFillStyle(COLORS.success, 0.8);
      } else {
        this.bg.setFillStyle(COLORS.cream);
      }
    });
    
    this.on('dragend', () => {
      this.isDragging = false;
      this.bg.setFillStyle(COLORS.cream);
      
      // Reset play zone description
      const gameScene = scene as any;
      if (gameScene.resetPlayZoneDescription) {
        gameScene.resetPlayZoneDescription();
      }
      
      if (this.y < 140) {
        // Check if can afford
        if (cardData.cost && RESOURCES.funds < cardData.cost) {
          this.showMessage('Not enough funds!', COLORS.danger);
          this.snapBack();
          return;
        }
        
        // Play the card
        this.playCard();
      } else {
        this.snapBack();
      }
    });
    
    // Initial depth - cards should always be above horses (which are depth 420-620)
    this.setDepth(500 + index);
  }

  setHomePosition(x: number, y: number): void {
    this.homeX = x;
    this.homeY = y;
  }

  private snapBack(): void {
    this.scene.tweens.add({
      targets: this,
      x: this.homeX,
      y: this.homeY,
      scaleX: 1,
      scaleY: 1,
      duration: 300,
      ease: 'Back.easeOut'
    });
  }

  private playCard(): void {
    // Execute card effect with RESOURCES
    this.cardData.effect(RESOURCES);
    
    // Show effect message with rarity flair
    const flair = this.cardData.rarity === 'legendary' ? '✨ ' : 
                  this.cardData.rarity === 'rare' ? '⭐ ' : '';
    this.showMessage(`${flair}${this.cardData.title}`, COLORS.success);
    
    // Special animation for legendary cards
    if (this.cardData.rarity === 'legendary') {
      // Burst of particles
      for (let i = 0; i < 12; i++) {
        const particle = this.scene.add.text(
          this.x + Phaser.Math.Between(-60, 60),
          this.y + Phaser.Math.Between(-60, 60),
          Phaser.Math.RND.pick(['✨', '⭐', '💫']),
          { fontSize: '28px' }
        );
        this.scene.tweens.add({
          targets: particle,
          y: particle.y - 120,
          alpha: 0,
          scale: 0.5,
          duration: 1000,
          onComplete: () => particle.destroy()
        });
      }
    }
    
    // Store scene reference before animation
    const sceneRef = this.scene;
    const cardDataRef = this.cardData;
    
    // Animate card being played
    sceneRef.tweens.add({
      targets: this,
      y: -250,
      alpha: 0,
      scaleX: 0.4,
      scaleY: 0.4,
      rotation: 0.2,
      duration: 450,
      ease: 'Power2',
      onComplete: () => {
        sceneRef.events.emit('cardPlayed', cardDataRef);
        this.destroy();
      }
    });
  }

  private showMessage(text: string, color: number = 0xFFFFFF): void {
    const hexColor = '#' + color.toString(16).padStart(6, '0');
    const msg = this.scene.add.text(GAME_WIDTH / 2, 220, text, {
      fontFamily: 'Courier New',
      fontSize: '24px',
      fontStyle: 'bold',
      color: hexColor,
      stroke: '#000000',
      strokeThickness: 4
    }).setOrigin(0.5);
    
    this.scene.tweens.add({
      targets: msg,
      y: 180,
      alpha: 0,
      duration: 1500,
      onComplete: () => msg.destroy()
    });
  }
}
